import { useState, useEffect, lazy, Suspense } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import Navbar from './components/Navbar'
import DonateModal from './components/DonateModal'
import ContactDrawer from './components/ContactDrawer'
import Home from './pages/Home'
import Footer from './components/Footer'
import FloatingWhatsApp from './components/FloatingWhatsApp'
import Lenis from 'lenis'

const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const TermsConditions = lazy(() => import('./pages/TermsConditions'))
const Awareness = lazy(() => import('./pages/Awareness'))
const WelfareActivities = lazy(() => import('./pages/WelfareActivities'))
const Admin = lazy(() => import('./pages/Admin'))
const NotFound = lazy(() => import('./pages/NotFound'))

const App = () => {
  const [isDonateOpen, setIsDonateOpen] = useState(false)
  const [isContactOpen, setIsContactOpen] = useState(false)
  const location = useLocation()
  const isAdminRoute = location.pathname === '/admin'

  // Smooth scroll initialization (disabled on admin panel route)
  useEffect(() => {
    if (isAdminRoute) return

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // easeOutExpo
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
    })

    let animationFrameId

    function raf(time) {
      lenis.raf(time)
      animationFrameId = requestAnimationFrame(raf)
    }

    animationFrameId = requestAnimationFrame(raf)

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId)
      lenis.destroy()
    }
  }, [isAdminRoute])

  // Smooth scroll hash routing helper for SPAs and automatic scroll-to-top on route change
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '')
      const el = document.getElementById(id)
      if (el) {
        // small timeout to allow page layout rendering to complete
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth' })
        }, 120)
      }
    } else {
      window.scrollTo(0, 0)
    }
  }, [location.pathname, location.hash])

  return (
    <div className="flex flex-col min-h-screen bg-brand-cream-50 font-sans selection:bg-brand-green-200 selection:text-brand-green-950">
      {/* Navigation */}
      {!isAdminRoute && (
        <Navbar 
          onOpenDonate={() => setIsDonateOpen(true)} 
          onOpenContact={() => setIsContactOpen(true)}
        />
      )}

      {/* Main Routing Content */}
      <main className="flex-grow">
        <Suspense
          fallback={
            <div className="min-h-[60vh] flex items-center justify-center text-slate-500 text-sm font-semibold">
              <span className="animate-pulse">Loading Chetak Animal Welfare Trust...</span>
            </div>
          }
        >
          <Routes>
            <Route 
              path="/" 
              element={
                <Home 
                  onOpenDonate={() => setIsDonateOpen(true)} 
                />
              } 
            />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-conditions" element={<TermsConditions />} />
            <Route path="/awareness" element={<Awareness />} />
            <Route 
              path="/welfare-activities" 
              element={
                <WelfareActivities 
                  onOpenDonate={() => setIsDonateOpen(true)}
                  onOpenContact={() => setIsContactOpen(true)}
                />
              } 
            />
            <Route 
              path="/welfare" 
              element={
                <WelfareActivities 
                  onOpenDonate={() => setIsDonateOpen(true)}
                  onOpenContact={() => setIsContactOpen(true)}
                />
              } 
            />
            <Route 
              path="/welfareactivities" 
              element={
                <WelfareActivities 
                  onOpenDonate={() => setIsDonateOpen(true)}
                  onOpenContact={() => setIsContactOpen(true)}
                />
              } 
            />
            <Route 
              path="/activities" 
              element={
                <WelfareActivities 
                  onOpenDonate={() => setIsDonateOpen(true)}
                  onOpenContact={() => setIsContactOpen(true)}
                />
              } 
            />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>

      {!isAdminRoute && (
        <Footer 
          onOpenContact={() => setIsContactOpen(true)} 
          onOpenDonate={() => setIsDonateOpen(true)} 
        />
      )}

      {/* Floating WhatsApp Quick Action Button */}
      {!isAdminRoute && <FloatingWhatsApp />}

      {/* Global Donation QR Modal */}
      <DonateModal 
        isOpen={isDonateOpen} 
        onClose={() => setIsDonateOpen(false)} 
      />

      {/* Slide-out Contact Drawer */}
      <ContactDrawer 
        isOpen={isContactOpen} 
        onClose={() => setIsContactOpen(false)} 
      />
    </div>
  )
}

export default App